import type { SkillDraft, SkillDraftField } from './types';

/**
 * Structural constants of the Skills MVP. No display text here — labels are
 * resolved from i18n by the components, so this file stays language-agnostic.
 */

/** Route segment of the section, under `assistants/:AssistantID`. */
// 'skills' belongs to features/agent-skills (VOX-182…192), which is the module
// the PRD is building out. This one keeps its own segment so both can be
// mounted at once and compared side by side; the Overview card that points
// here is labelled "Skills (MVP)" for the same reason.
export const SKILLS_ROUTE_SEGMENT = 'skills-mvp';

/** Path of the editor for a new skill, relative to the section. */
export const NEW_SKILL_SEGMENT = 'new';

/**
 * What a skill cannot be saved without.
 *
 * Only the name, and not by preference: AGE-270 makes the list show *nothing but
 * the name*, so a skill saved without one would be an unidentifiable row. The
 * description and the instructions can be filled in later — the ticket asks Save
 * to persist them, not to demand them.
 */
export const REQUIRED_SKILL_FIELDS: SkillDraftField[] = ['name'];

/** A brand-new skill: empty until it is saved. */
export const EMPTY_SKILL_DRAFT: SkillDraft = {
  name: '',
  description: '',
  instructions: '',
};

/** Placeholder rows the list skeleton renders while the first fetch resolves. */
export const SKELETON_ROWS = 4;

/* ─── Limits the backend enforces ───────────────────────────────────────── */

/**
 * The caps the API rejects with a 400. Mirrored here so the UI can stop a save
 * before the round-trip and say which limit was hit, instead of surfacing a
 * validation error the user cannot map back to a field.
 */
export const SKILL_LIMITS = {
  /** Skills an agent may have. */
  maxSkills: 20,
  maxName: 100,
  maxDescription: 500,
  maxInstructions: 40000,
  /** Combined size of every field across every skill. */
  maxTotalCharacters: 80000,
  /** `skill_id` length, both ends. */
  minId: 1,
  maxId: 64,
} as const;
